export type LogCardCopy = typeof defaultLogCardCopy;

export const defaultLogCardCopy = {
	copied: "Copied",
	imageGenerated: "Image generated",
	imageGeneratedViewDetails: "Image generated - view details",
	inputImageTitle: "Input Image",
	generatedImageTitle: "Generated Image",
	dateFormat: "dd.MM.yyyy HH:mm:ss",
	toolCalls: (names: string) => `Tool calls: ${names}`,
	toolCallsExecuted: "Tool calls executed",
	finishReason: (reason: string | null | undefined) => reason ?? "-",
	retentionTooltip:
		"Enable retention in organization policies to store response content",
	retried: "Retried",
	viewDetails: "View details",
	toggleDetails: "Toggle details",
	cacheStatus: {
		fully: "Fully cached",
		partial: "Partially cached",
		none: "Not cached",
	},
	cachedTokens: (tokens: string | number) => `(${tokens} cached)`,
	tokenCount: (tokens: string | number | null | undefined) =>
		`${tokens} tokens`,
	providerCost: "Provider cost",
	notDeductedFromYourBalance: " - not deducted from your balance",
	notDeductedFromBalance: " - not deducted from balance",
	discountOff: (percent: string) => `${percent}% off`,
	pluginCount: (count: number) => `${count} plugin${count > 1 ? "s" : ""}`,
	requestDetails: "Request Details",
	projectId: "Project ID",
	apiKey: "API Key",
	requestedModel: "Requested Model",
	usedModel: "Used Model",
	usedModelProviderMapping: "Used Model Provider Mapping",
	provider: "Provider",
	routingInfo: "Routing Info",
	selection: "Selection",
	key: "Key",
	set: "set",
	unset: "unset",
	active: "active",
	available: "Available",
	scores: "Scores",
	rpmCapped: "rpm capped",
	contentFilter: "content filter",
	cache: "cache",
	requestAttempts: "Request Attempts",
	log: "log",
	ok: "ok",
	responseMetrics: "Response Metrics",
	duration: "Duration",
	throughput: "Throughput",
	timeToFirstToken: "Time to First Token",
	timeToFirstReasoningToken: "Time to First Reasoning Token",
	responseSize: "Response Size",
	unknown: "Unknown",
	bytes: "bytes",
	promptTokens: "Prompt Tokens",
	completionTokens: "Completion Tokens",
	totalTokens: "Total Tokens",
	cachedInputTokens: "Cached Input Tokens",
	cacheWriteTokens: "Cache Write Tokens",
	reasoningTokens: "Reasoning Tokens",
	imageInputTokens: "Image Input Tokens",
	imageOutputTokens: "Image Output Tokens",
	audioInputTokens: "Audio Input Tokens",
	originalFinishReason: "Original Finish Reason",
	unifiedFinishReason: "Unified Finish Reason",
	streamed: "Streamed",
	canceled: "Canceled",
	cached: "Cached",
	yes: "Yes",
	no: "No",
	costInformation: "Cost Information",
	providerPricing: "Provider pricing",
	inputCost: "Input Cost",
	outputCost: "Output Cost",
	cachedInputCost: "Cached Input Cost",
	cacheWriteCost: "Cache Write Cost",
	requestCost: "Request Cost",
	nativeWebSearchCost: "Native Web Search Cost",
	contentFilterCost: "Content Filter Cost",
	imageInputCost: "Image Input Cost",
	imageOutputCost: "Image Output Cost",
	videoOutputCost: "Video Output Cost",
	audioInputCost: "Audio Input Cost",
	inferenceTotal: "Inference Total",
	discountApplied: "Discount Applied",
	pricingTier: "Pricing Tier",
	serviceTier: "Service Tier",
	billedToYourOrganization: "Billed to your organization",
	billedToOrganization: "Billed to organization",
	dataStorage: "Data Storage",
	metadata: "Metadata",
	date: "Date",
	requestId: "Request ID",
	traceId: "Trace ID",
	sessionId: "Session ID",
	logId: "Log ID",
	source: "Source",
	userAgent: "User Agent",
	project: "Project",
	organization: "Organization",
	mode: "Mode",
	usedMode: "Used Mode",
	copyRequestId: "Copy request ID",
	copyTraceId: "Copy trace ID",
	copySessionId: "Copy session ID",
	copyLogId: "Copy log ID",
	copyProjectId: "Copy project ID",
	copyOrganizationId: "Copy organization ID",
	copyApiKeyId: "Copy API key ID",
	customHeaders: "Custom Headers",
	modelParameters: "Model Parameters",
	temperature: "Temperature",
	temperatureDescription:
		"Controls randomness: higher values produce more random outputs",
	maxTokens: "Max Tokens",
	maxTokensDescription: "Maximum number of tokens to generate",
	topP: "Top P",
	topPDescription:
		"Alternative to temperature, controls diversity via nucleus sampling",
	frequencyPenalty: "Frequency Penalty",
	frequencyPenaltyDescription:
		"Decreases the likelihood of repeating the same tokens",
	reasoningEffort: "Reasoning Effort",
	reasoningEffortDescription:
		"Requested chain-of-thought effort for reasoning-capable models",
	reasoningBudget: "Reasoning Budget",
	reasoningBudgetDescription:
		"Exact token budget allocated for reasoning (max_tokens in reasoning config)",
	effort: "Effort",
	effortDescription: "Controls the computational effort for supported models",
	responseFormat: "Response Format",
	responseFormatDescription:
		"Requested output format (text, json_object, or json_schema)",
	aspectRatio: "Aspect Ratio",
	imageSize: "Image Size",
	imageQuality: "Image Quality",
	imageCount: "Image Count",
	outputFormat: "Output Format",
	compression: "Compression",
	seed: "Seed",
	plugins: "Plugins",
	pluginResults: "Plugin Results",
	responseHealing: "Response Healing:",
	applied: "Applied",
	notNeededValidJson: "Not needed (valid JSON)",
	additionalParameters: "Additional Parameters",
	toolInformation: "Tool Information",
	availableTools: "Available Tools",
	toolChoice: "Tool Choice",
	toolCallsTitle: "Tool Calls",
	unknownFunction: "Unknown Function",
	idLabel: "ID",
	notAvailable: "N/A",
	arguments: "Arguments:",
	builtinTools: "Builtin Tools",
	nativeWebSearch: "Native Web Search",
	webSearchUsed: "Web search was used in this request",
	cost: "Cost",
	errorDetails: "Error Details",
	statusCode: "Status Code",
	statusText: "Status Text",
	errorMessage: "Error Message",
	requestRetriedSucceeded: "This request was retried and succeeded.",
	viewSuccessfulRequest: "View successful request",
	messageContext: "Message Context",
	previewInput: "Preview Input",
	base64ImageData: "[base64 image data]",
	messageDataNotRetained:
		"Message data not retained. Enable retention in organization policies to store request messages.",
	noMessageData: "No message data available.",
	reasoningContent: "Reasoning Content",
	response: "Response",
	previewImage: "Preview Image",
	imageGeneratedSentence: "Image generated.",
	viewImage: "View image",
	responseContentNotRetained:
		"Response content not retained. Enable retention in organization policies to store response data.",
	noResponseContent: "No response content available.",
	previewOfImages: (title: string, count: number) =>
		`Preview of the ${title.toLowerCase()}${count > 1 ? "s" : ""} from this request.`,
	imageAlt: (index: number) => `Generated image ${index}`,
};
